import {Button, Col, Divider, Form, Input, Row, Space, Switch} from "antd";
import {RefSelectProps} from "antd/lib/select";
import React, {FC, ReactNode, useMemo, useState} from "react";
import Icon from "../../assets/icons/icon";
import TaxCaseStore from "../../contexts/taxCaseStore";
import _t from "../../lang/translate";
import {countriesAutoComplete} from "../../services/autocompleteService";
import {getValuationValue, patchVehicle} from "../../services/vehicleService";
import {Vehicle} from "../../types/appTypes";
import consts from "../../utilities/consts";
import format from "../../utilities/formatNumbers";
import {constructObjectRecursively} from "../../utilities/objectUtil";
import {isValidNum} from "../../utilities/typeGuard";
import AutoCalcSwitch from "../form/autoCalcSwitch";
import DateSelect from "../form/dateSelect";
import EnumRadio from "../form/EnumRadio";
import EnumSelect from "../form/enumSelect";
import InputNum from "../form/inputNum";
import InputPrice from "../form/inputPrice";
import InputVin from "../form/inputVin";
import LinkBox from "../form/linkBox";
import {renderErrorMessage} from "../messages/errorMessage";
import PopMaybeOver from "../popMaybeOver";
import ServerSelectBox from "../serverSelectBox";
import {printEmissionCategory} from "./vehicleInstanceData";


interface EditVehicleInstanceFormProps {
  vehicle: Vehicle | null;
  nested?: string[];
  disabled?: boolean;
  required?: boolean;
  hideVehicleType?: boolean;
  showListingLocation?: boolean;
  showMotorAtlas?: boolean;
  motorAtlasIdRef?: React.Ref<RefSelectProps>
  setIsEditing: (editing: boolean) => void;
}

function getBatteryTypeConstant(batteryType: string | null | undefined) {
  switch (batteryType) {
    case "LFP": return 8.5; // Lithium-jern-fosfat – stabil, tungere pr. kWh
    case "NMC": return 7.0; // Nickel-Mangan-Cobalt – mest udbredt i EU
    case "LIION": return 7.5; // Generisk Li-ion (ukendt kemi)
    case "LIPOLY": return 8.0; // Lithium-polymer – lavere energitæthed
    case "NIMH": return 13.0; // Nickel-metal-hydrid – ældre hybridteknologi
    default: return 7.5; // Fallback-værdi for ukendt type
  }
}

const EditVehicleInstanceData: FC<EditVehicleInstanceFormProps> = ({
  vehicle,
  disabled,
  hideVehicleType,
  required = true,
  showListingLocation,
  setIsEditing
}) => {

  const { taxCase, setTaxCase } = TaxCaseStore.useContainer()
  const [isSaving, setIsSaving] = useState(false)
  const [vinDisabled, setVinDisabled] = useState(true)
  const [form] = Form.useForm<Vehicle>()
  const vehicleInitialValues = useMemo(() => {
    return {
      ...vehicle ?? {},
      emissionAmount: vehicle?.emissionAmount ?? vehicle?.remoteData?.dmr?.mapped?.emissionAmount,
      emissionUnitId: vehicle?.emissionUnitId ?? vehicle?.remoteData?.dmr?.mapped?.emissionUnitId,
      emissionUnit: vehicle?.emissionUnit ?? vehicle?.remoteData?.dmr?.mapped?.emissionUnit,
      autoCalcBatteryWeight: true,
    }
  }, [vehicle])


  // TODO: Detect if vin changed,
  // If yes, then call saveTaxVehicle first,
  // else proceed as normal.
  //
  const handleSave = async (values: Vehicle) => {
    try {
      if (!vehicle?.id) {
        throw new Error("Ingen køretøjs-ID fundet. Kontakt udvikler.")
      }
      setIsSaving(true)
      const { data: newVehicle } = await patchVehicle(vehicle.id, values)
      setTaxCase({
        ...taxCase, vehicle: newVehicle
      });
      setIsEditing(false)
    } catch (error) {
      renderErrorMessage(error);
      setIsSaving(false)
    }
  };

  const handleValuesChange = (changedValues: Partial<Vehicle>, values: Vehicle) => {
    if (changedValues.emissionAmount === 0 && !values.batteryCapacity) {
      form.setFieldsValue({ batteryCapacity: 50 })
    }
    if ("autoCalcBatteryWeight" in changedValues || "batteryCapacity" in changedValues) {
      if ("autoCalcBatteryWeight" in values && values.autoCalcBatteryWeight) {
        if (isValidNum(values.batteryCapacity)) {
          const constant = getBatteryTypeConstant(vehicle?.remoteData?.dat?.raw?.batteryType);
          const batteryWeight = (constant * values.batteryCapacity) + 17;
          form.setFieldsValue({ batteryWeight: batteryWeight })
        } else {
          form.setFieldsValue({ batteryWeight: null })
        }
      }
    }
  }

  return (
    <Form form={form}
      {...consts.formItemProps}
      className="m-0 form-items-mb-05"
      initialValues={vehicleInitialValues}
      requiredMark={false}
      onFinish={handleSave}
      onValuesChange={handleValuesChange}

    >
      {vehicle?.link && (
        <Form.Item label={_t("link")} className="form-items-min-w-0" shouldUpdate labelCol={{ span: 3 }}
          labelAlign="left" style={{ flexWrap: "nowrap" }} wrapperCol={{ span: 21 }}>
          {({ setFieldsValue }) => {
            return (
              <LinkBox
                link={vehicle.link ?? ''}
                onFetch={(data) => {
                  const linkVehicle = { ...data.vehicle };
                  linkVehicle.typeId = vehicle?.typeId;
                  setFieldsValue(linkVehicle);
                }}
              />
            );
          }}
        </Form.Item>
      )}

      <Form.Item
        name={"typeId"}
        label={_t("vehicle_type")}
        rules={[{ required }]}
        hidden={!!hideVehicleType}
      >
        <EnumRadio disabled={disabled || isSaving} size="small" nameKey={["vehicle", "type"]} />
      </Form.Item>
      <Form.Item
        name={"isNew"}
        label={_t("new", "vehicle") + "?"}
        valuePropName="checked"
      >
        <Switch disabled={disabled || isSaving} size="small" />
      </Form.Item>
      <Form.Item
        name="isDanish"
        label={_t("is_danish")}
        valuePropName="checked"
        rules={[{ required: true, message: _t("msg.field_required") }]}
      >
        <Switch disabled={disabled || isSaving} size="small" />
      </Form.Item>



      <Form.Item label={_t("vin")}>
        <Space>
          <Form.Item name={"vin"} noStyle>
            <InputVin disabled={vinDisabled} />
          </Form.Item>
          <Button onClick={() => setVinDisabled(!vinDisabled)}
            icon={<Icon name={vinDisabled ? "lock-closed-outline" : "lock-open-outline"} />}
          />
        </Space>
      </Form.Item>

      <Form.Item
        name={"firstRegDate"}
        label={_t("firstRegDate")}
        rules={[{ required }]}
      >
        <DateSelect disabled={disabled || isSaving} />
      </Form.Item>


      <Form.Item name={"mileage"} label={_t("mileage")} rules={[{ required }]}>
        <InputNum disabled={disabled || isSaving} step={1000} min={0} suffix={_t("mileage_unit")} />
      </Form.Item>

      <Divider className="mt-1 mb-1" />

      <Form.Item
        name={"qualityId"}
        label={_t("car_condition")}
        rules={[{ required }]}
      >
        <EnumSelect defaultActiveFirstOption disabled={disabled || isSaving} nameKey={["vehicle", "quality"]} />
      </Form.Item>
      <Form.Item
        name={"useCaseId"}
        label={_t("special_use")}
        rules={[{ required }]}
      >
        <EnumSelect defaultActiveFirstOption disabled={disabled || isSaving} nameKey={["vehicle", "useCase"]} />
      </Form.Item>
      <Form.Item shouldUpdate noStyle>
        {({ getFieldValue }) =>
          getFieldValue("typeId") !== 2 ? null : (
            <Form.Item
              name={"isHighWeight"}
              label={_t("over_3000_kg")}
              valuePropName="checked"
            >
              <Switch disabled={disabled || isSaving} size="small" />
            </Form.Item>
          )
        }
      </Form.Item>
      <Form.Item name={"emissionAmount"} label={_t("co2_emission")} rules={[{ required }]}>
        <InputPrice disabled={disabled || isSaving} decimalSize={2} suffix={_t("emission_unit")} maxLength={6} />
      </Form.Item>
      <Form.Item
        rules={[{ required }]}
        name={"emissionUnitId"}
        label={_t("co2_measurement")}
      >
        <EnumRadio disabled={disabled || isSaving} size="small" nameKey={["vehicle", "emissionUnit"]} />
      </Form.Item>
      <Form.Item
        name={"batteryCapacity"}
        label={_t("battery_capacity")}
      >
        <InputPrice disabled={disabled || isSaving} suffix={_t("battery_unit")} max={1000} />

      </Form.Item>

      <Form.Item label={_t("battery_weight")} shouldUpdate>
        {({ getFieldValue }) => {
          const autoCalc = getFieldValue('autoCalcBatteryWeight')
          return (
            <Row wrap={false}>
              <Col flex="auto">
                <Form.Item name={"batteryWeight"} >
                  <InputPrice disabled={disabled || isSaving || autoCalc} suffix={"kg"} />
                </Form.Item>
              </Col>
              <Col flex="40px" className="text-right">
                <Form.Item name="autoCalcBatteryWeight" noStyle>
                  <AutoCalcSwitch />
                </Form.Item>
              </Col>
            </Row>
          )
        }}
      </Form.Item>


      {/*<Form.Item name={"electricConsumption"} label={_t("battery_usage")}>
        <InputPrice disabled={disabled || isSaving} suffix={_t("battery_usage_unit")} max={1000} />
      </Form.Item>
      <Form.Item name={"electricRange"} label={_t("battery_range")}>
        <InputNum disabled={disabled || isSaving} suffix={_t("mileage_unit")} maxLength={4} />
      </Form.Item>*/}

      {showListingLocation && (
        <>
          <Divider>{_t("carLocation")}</Divider>

          <Form.Item
            name={["listingLocation", "countryId"]}
            label={_t("country")}
          >
            <ServerSelectBox disabled={disabled || isSaving} apiUrl={countriesAutoComplete()} />
          </Form.Item>

          <Form.Item
            name={["listingLocation", "zip"]}
            label={_t("zip")}
          >
            <Input disabled={disabled || isSaving} />
          </Form.Item>

          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue, setFieldsValue }) => {
              const vin = getFieldValue("vin")
              const firstRegDate = getFieldValue("firstRegDate")
              const mileage = getFieldValue("mileage")

              return (
                <ListingLocationPrice vin={vin} firstRegDate={firstRegDate} mileage={mileage} setFieldsValue={setFieldsValue} />
              )
            }}
          </Form.Item>


          <Form.Item name={"isVatDead"} label={_t("vat_dead_vehicle")} valuePropName="checked">
            <Switch disabled={disabled} />
          </Form.Item>

        </>
      )}

      <div className="mt-1" style={{ display: "flex", justifyContent: "space-between" }}>
        <Button disabled={!checkCanLeave(vehicle)} onClick={() => setIsEditing(false)} >
          {_t("stop", "editing")}
        </Button>
        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue }) => {
            const emissionAmount = getFieldValue(['emissionAmount'])
            const dmrEmission = vehicle?.remoteData?.dmr?.mapped?.emissionAmount
            const datEmission = vehicle?.remoteData?.dat?.mapped?.emissionAmount
            return (
              <EmissionCategoryWarningPopover datEmission={datEmission} dmrEmission={dmrEmission} emissionAmount={emissionAmount}>
                <Button type="primary" htmlType="submit">{_t("save", "vehicle")}</Button>
              </EmissionCategoryWarningPopover>
            )

          }}
        </Form.Item>
      </div>


    </Form >
  );
};

export function emissionCategoryWarningCheck({ datEmission, dmrEmission, emissionAmount }: {
  emissionAmount: number | null | undefined
  dmrEmission: number | null | undefined
  datEmission: number | null | undefined
}) {
  const cat = printEmissionCategory(emissionAmount)
  const dmrCat = printEmissionCategory(dmrEmission)
  const datCat = printEmissionCategory(datEmission)

  const sameDat = datCat === cat;
  const sameDmr = dmrCat === cat;
  const sameDmrDat = datCat === dmrCat;
  const hasBoth = dmrCat !== '-' && datCat !== '-';
  const emissionCategoryShouldWarn = hasBoth && (!sameDmrDat || !sameDmr || !sameDat);

  return {
    emissionCategoryShouldWarn, sameDmrDat, sameDat, sameDmr,
    cat, dmrCat, datCat
  }
}
export function EmissionCategoryWarningPopover({ children, datEmission, dmrEmission, emissionAmount }: {
  emissionAmount: number | null | undefined
  dmrEmission: number | null | undefined
  datEmission: number | null | undefined
  children: ReactNode
}) {
  const { cat, datCat, dmrCat, sameDat, sameDmrDat, sameDmr } = emissionCategoryWarningCheck({ emissionAmount, datEmission, dmrEmission })
  let title = null;
  let content = null;
  if (!sameDat || !sameDmr) {
    title = "Er du sikker?"
    content = (
      <div>
        <span className="d-block mb-05 text-medium">
          CO2-emission for DMR eller DAT passer ikke
        </span>
        <Row wrap={false} className={`${!(sameDat || sameDmr) ? "text-danger" : ""}`}>
          <Col flex="120px">CO2-emission:</Col>
          <Col flex="56px">{format.emission(emissionAmount)}</Col>
          <Col>{cat}</Col>
        </Row>
        <Row wrap={false} className={`${!sameDmrDat ? "text-danger" : ""}`} >
          <Col flex="120px">For DMR:</Col>
          <Col flex="56px">{format.emission(dmrEmission)}</Col>
          <Col>{dmrCat}</Col>
        </Row>
        <Row wrap={false} className={`${!sameDmrDat ? "text-danger" : ""}`}>
          <Col flex="120px">For DAT:</Col>
          <Col flex="56px">{format.emission(datEmission)}</Col>
          <Col>{datCat}</Col>
        </Row>
      </div>
    )
  }
  return (

    <PopMaybeOver title={title} content={content} overlayInnerStyle={{ width: 360 }}>
      {children}
    </PopMaybeOver>
  )
}

export default EditVehicleInstanceData;

type ListingLocationPriceProps = {
  vin: string,
  mileage: number,
  firstRegDate: string,
  setFieldsValue: (value: any) => void
}
function ListingLocationPrice({ firstRegDate, mileage, vin, setFieldsValue }: ListingLocationPriceProps) {
  const [loading, setLoading] = useState(false)
  async function handleGetValuationValue() {
    if (!vin || !firstRegDate || !mileage) {
      renderErrorMessage("Operationen har brug for stelnummer, 1. reg. dato og kilometerstand");
      return;
    }
    setLoading(true)

    try {
      const { data: { data } } = await getValuationValue(vin, {
        dataPoint: "salesPrice",
        firstRegDate: firstRegDate,
        mileage: mileage
      })
      if (data) {
        const res = constructObjectRecursively(["dealerSalesPrice"], data)
        setFieldsValue(res)
      }
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      setLoading(false)
    }

  }

  return (
    <Row gutter={[8, 8]} className="flex-nowrap">
      <Col flex="auto">
        <Form.Item name={"dealerSalesPrice"} label={_t("dealer_sales", "price")}>
          <InputPrice decimalSize={0} suffix={"kr"} maxLength={10} />
        </Form.Item>
      </Col>
      <Col><Button loading={loading} onClick={handleGetValuationValue} icon={<Icon name="refresh-outline" />} /></Col>
    </Row>
  )
}

function checkCanLeave(values: Vehicle | null | undefined) {
  const emission = values?.emissionAmount
  if (!isValidNum(emission)) return false;
  return true
}
